import { isSupabaseConfigured, supabase } from '../lib/supabase.js'
import { demoLeads, demoStages, demoUsers } from '../data/demo.js'

const clone = value => structuredClone(value)
let activeOrganizationId = ''
const demoServices = ['Gestão de Tráfego', 'Consultoria Comercial', 'Google Ads', 'Meta Ads', 'Criação de Landing Page', 'Automação', 'Outro']
  .map((name, position) => ({ id: `demo-service-${position}`, name, position, active: true }))
const demoLabels = [
  { id: 'demo-label-priority', name: 'Prioridade', color: '#ef4444', active: true },
  { id: 'demo-label-proposal', name: 'Proposta enviada', color: '#8b5cf6', active: true },
  { id: 'demo-label-waiting', name: 'Aguardando resposta', color: '#f59e0b', active: true },
]

export async function getSession() {
  if (!isSupabaseConfigured) return { demo: true, user: { email: 'demo@caminhodavenda.com.br' } }
  const { data, error } = await supabase.auth.getSession()
  if (error) throw error
  return data.session
}

export async function signIn(email, password) {
  if (!isSupabaseConfigured) return { user: { email } }
  const { data, error } = await supabase.auth.signInWithPassword({ email, password })
  if (error) throw error
  return data
}

export async function requestPasswordReset(email) {
  if (!isSupabaseConfigured) return
  const redirectTo = `${window.location.origin}/?auth=recovery`
  const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo })
  if (error) throw error
}

export async function updatePassword(password) {
  if (!isSupabaseConfigured) return
  const { error } = await supabase.auth.updateUser({ password })
  if (error) throw error
}

export async function resendConfirmation(email) {
  if (!isSupabaseConfigured) return
  const { error } = await supabase.auth.resend({ type: 'signup', email, options: { emailRedirectTo: window.location.origin } })
  if (error) throw error
}

export function isPasswordRecoveryFlow() {
  const params = new URLSearchParams(window.location.search)
  return params.get('auth') === 'recovery' || window.location.hash.includes('type=recovery') || window.location.hash.includes('type=invite')
}

export function subscribeToAuthChanges(callback) {
  if (!isSupabaseConfigured) return () => {}
  const { data } = supabase.auth.onAuthStateChange((event, session) => callback(event, session))
  return () => data.subscription.unsubscribe()
}

export async function signOut() {
  if (isSupabaseConfigured) await supabase.auth.signOut()
}

export async function loadWorkspace() {
  if (!isSupabaseConfigured) return {
    stages: clone(demoStages),
    users: clone(demoUsers),
    leads: clone(demoLeads),
    services: clone(demoServices),
    labels: clone(demoLabels),
    stageHistory: demoLeads.map((lead, index) => ({
      id: `demo-history-${index}`,
      lead_id: lead.id,
      from_stage_id: null,
      to_stage_id: lead.stage_id,
      changed_at: lead.created_at,
    })),
  }
  const { data: authData, error: authError } = await supabase.auth.getUser()
  if (authError || !authData.user) throw authError || new Error('Sua sessão expirou. Entre novamente.')
  const { data: memberships, error: membershipError } = await supabase
    .from('organization_members')
    .select('organization_id, role')
    .eq('user_id', authData.user.id)
    .eq('active', true)
    .limit(1)
  if (membershipError) throw membershipError
  if (!memberships?.length) throw new Error('Seu usuário ainda não está vinculado a uma organização.')
  activeOrganizationId = memberships[0].organization_id
  const [{ data: pipelines, error: pError }, { data: members, error: mError }, { data: channels, error: cError }, { data: services, error: serviceError }, { data: labels, error: labelError }] = await Promise.all([
    supabase.from('pipelines').select('*').eq('organization_id', activeOrganizationId).eq('active', true).order('created_at'),
    supabase.from('organization_members').select('user_id, role, profiles:user_id(id, full_name, avatar_url)').eq('organization_id', activeOrganizationId).eq('active', true),
    supabase.from('whatsapp_channels').select('id, instance_name, phone_e164, status, status_message, connected_at').eq('organization_id', activeOrganizationId).eq('active', true).order('created_at').limit(1),
    supabase.from('services').select('*').eq('organization_id', activeOrganizationId).order('position').order('name'),
    supabase.from('whatsapp_labels').select('*').eq('organization_id', activeOrganizationId).order('name'),
  ])
  if (pError || mError || cError || serviceError || labelError) throw pError || mError || cError || serviceError || labelError
  const pipeline = pipelines.find(item => item.is_default) || pipelines[0]
  if (!pipeline) throw new Error('Nenhuma pipeline ativa foi encontrada.')
  const [{ data: stages, error: sError }, { data: leads, error: lError }, { data: stageHistory, error: hError }] = await Promise.all([
    supabase.from('pipeline_stages').select('*').eq('pipeline_id', pipeline.id).eq('active', true).order('position'),
    supabase.from('leads').select('*').eq('organization_id', activeOrganizationId).eq('pipeline_id', pipeline.id).is('deleted_at', null).order('created_at', { ascending: false }),
    supabase.from('lead_stage_history').select('id, lead_id, from_stage_id, to_stage_id, changed_at').eq('organization_id', activeOrganizationId).order('changed_at'),
  ])
  if (sError || lError || hError) throw sError || lError || hError
  const pipelineStageIds = new Set(stages.map(stage => stage.id))
  return {
    organizationId: activeOrganizationId,
    currentRole: memberships[0].role,
    pipeline,
    stages,
    leads,
    stageHistory: (stageHistory || []).filter(item => pipelineStageIds.has(item.to_stage_id) || pipelineStageIds.has(item.from_stage_id)),
    services: services || [],
    labels: labels || [],
    users: members.map(m => ({ ...m.profiles, role: m.role })),
    whatsappChannel: channels?.[0] || null,
  }
}

export async function saveLead(lead, context) {
  if (!isSupabaseConfigured) return { ...lead, id: lead.id || crypto.randomUUID(), created_at: lead.created_at || new Date().toISOString() }
  const { data: authData, error: authError } = await supabase.auth.getUser()
  if (authError || !authData.user) throw authError || new Error('Sua sessão expirou. Entre novamente.')
  const payload = {
    organization_id: context.organizationId,
    pipeline_id: context.pipeline.id,
    owner_id: lead.owner_id || null,
    name: lead.name,
    company_name: lead.company_name || null,
    email: lead.email || null,
    phone: lead.phone || null,
    whatsapp_contact_id: lead.whatsapp_contact_id || null,
    source_name: lead.source_name?.trim() || 'Manual',
    service_type: lead.service_type || null,
    service_id: lead.service_id || null,
    estimated_value: Number(lead.estimated_value) || 0,
    priority: lead.priority || 'medium',
    next_activity_title: lead.next_activity_title || null,
    next_activity_at: lead.next_activity_at || null,
  }
  if (!lead.id) {
    payload.stage_id = lead.stage_id
    payload.created_by = authData.user.id
  }
  const query = lead.id
    ? supabase.from('leads').update(payload).eq('id', lead.id)
    : supabase.from('leads').insert(payload)
  const { data, error } = await query.select().single()
  if (error) throw error
  return data
}

export async function deleteLead(leadId, context) {
  if (!isSupabaseConfigured) return { id: leadId, deleted_at: new Date().toISOString() }
  const { data: authData, error: authError } = await supabase.auth.getUser()
  if (authError || !authData.user) throw authError || new Error('Sua sessão expirou. Entre novamente.')
  const { data, error } = await supabase.from('leads').update({ deleted_at: new Date().toISOString(), deleted_by: authData.user.id }).eq('id', leadId).eq('organization_id', context.organizationId).select().single()
  if (error) throw error
  return data
}

export async function saveService(service, context) {
  if (!isSupabaseConfigured) return { ...service, id: service.id || crypto.randomUUID(), active: true }
  const payload = {
    organization_id: context.organizationId,
    name: service.name.trim(),
    description: service.description?.trim() || null,
    position: Number.isInteger(service.position) ? service.position : context.services.length,
    active: true,
  }
  if (!service.id) {
    const { data: authData, error: authError } = await supabase.auth.getUser()
    if (authError || !authData.user) throw authError || new Error('Sua sessão expirou. Entre novamente.')
    payload.created_by = authData.user.id
  }
  const query = service.id
    ? supabase.from('services').update(payload).eq('id', service.id).eq('organization_id', context.organizationId)
    : supabase.from('services').insert(payload)
  const { data, error } = await query.select().single()
  if (error) throw error
  return data
}

export async function archiveService(serviceId, context) {
  if (!isSupabaseConfigured) return { id: serviceId, active: false }
  const { data, error } = await supabase.from('services').update({ active: false }).eq('id', serviceId).eq('organization_id', context.organizationId).select().single()
  if (error) throw error
  return data
}

export async function saveLabel(label, context) {
  if (!isSupabaseConfigured) return { ...label, id: label.id || crypto.randomUUID(), active: true }
  const payload = {
    organization_id: context.organizationId,
    name: label.name.trim(),
    color: label.color || '#64748b',
    active: true,
  }
  if (!label.id) {
    const { data: authData, error: authError } = await supabase.auth.getUser()
    if (authError || !authData.user) throw authError || new Error('Sua sessão expirou. Entre novamente.')
    payload.created_by = authData.user.id
  }
  const query = label.id
    ? supabase.from('whatsapp_labels').update(payload).eq('id', label.id).eq('organization_id', context.organizationId)
    : supabase.from('whatsapp_labels').insert(payload)
  const { data, error } = await query.select().single()
  if (error?.code === '23505') throw new Error('Já existe uma etiqueta com esse nome.')
  if (error) throw error
  return data
}

export async function archiveLabel(labelId, context) {
  if (!isSupabaseConfigured) return { id: labelId, active: false }
  const { data, error } = await supabase.from('whatsapp_labels').update({ active: false }).eq('id', labelId).eq('organization_id', context.organizationId).select().single()
  if (error) throw error
  return data
}

export async function moveLead(id, stageId, reason, note = '', closingValue = null) {
  if (!isSupabaseConfigured) return { id, stage_id: stageId, ...(closingValue ? { won_value: closingValue } : {}) }
  const { data, error } = await supabase.rpc('move_lead_with_reason', {
    p_lead_id: id,
    p_to_stage_id: stageId,
    p_reason: reason,
    p_note: note || null,
    p_closing_value: closingValue,
  }).single()
  if (error) throw error
  return data
}

export async function saveStage(stage, context) {
  if (!isSupabaseConfigured) return { ...stage, id: stage.id || crypto.randomUUID(), position: stage.position ?? context.stages.length }
  const payload = {
    organization_id: context.organizationId,
    pipeline_id: context.pipeline.id,
    name: stage.name.trim(),
    color: stage.color,
    kind: stage.kind,
    probability: Number(stage.probability) || 0,
    active: true,
  }
  if (!stage.id) {
    const { data: lastStage, error: positionError } = await supabase
      .from('pipeline_stages')
      .select('position')
      .eq('pipeline_id', context.pipeline.id)
      .order('position', { ascending: false })
      .limit(1)
      .maybeSingle()
    if (positionError) throw positionError
    payload.position = (lastStage?.position ?? -1) + 1
  }
  const query = stage.id
    ? supabase.from('pipeline_stages').update(payload).eq('id', stage.id)
    : supabase.from('pipeline_stages').insert(payload)
  const { data, error } = await query.select().single()
  if (error) throw error
  return data
}

export async function archiveStage(stageId) {
  if (!isSupabaseConfigured) return
  const { error } = await supabase.from('pipeline_stages').delete().eq('id', stageId)
  if (error) throw error
}

export async function reorderStages(stageIds, pipelineId) {
  if (!isSupabaseConfigured) return
  const { error } = await supabase.rpc('reorder_pipeline_stages', {
    p_pipeline_id: pipelineId,
    p_stage_ids: stageIds,
  })
  if (error) throw error
}

export async function saveCrmUser(user, context) {
  if (!isSupabaseConfigured) return { ...user, id: user.id || crypto.randomUUID() }
  if (user.id) {
    const { data, error } = await supabase.functions.invoke('admin-users', { body: { action: 'update', organizationId: context.organizationId, userId: user.id, fullName: user.full_name.trim(), role: user.role } })
    if (error) {
      let message = error.message
      try { message = (await error.context?.json())?.error || message } catch { /* resposta sem JSON */ }
      throw new Error(message)
    }
    if (!data?.user) throw new Error(data?.error || 'Não foi possível atualizar o usuário.')
    return { ...user, ...data.user }
  }
  const { data, error } = await supabase.functions.invoke('admin-users', {
    body: {
      action: 'invite',
      organizationId: context.organizationId,
      email: user.email.trim().toLowerCase(),
      fullName: user.full_name.trim(),
      role: user.role,
    },
  })
  if (error) {
    let message = error.message
    try { message = (await error.context?.json())?.error || message } catch { /* resposta sem JSON */ }
    throw new Error(message)
  }
  if (!data?.user) throw new Error(data?.error || 'Não foi possível convidar o usuário.')
  return data.user
}

export async function removeCrmUser(userId, context) {
  if (!isSupabaseConfigured) return { ok: true, userId }
  const { data, error } = await supabase.functions.invoke('admin-users', { body: { action: 'remove', organizationId: context.organizationId, userId } })
  if (error) {
    let message = error.message
    try { message = (await error.context?.json())?.error || message } catch { /* resposta sem JSON */ }
    throw new Error(message)
  }
  if (!data?.ok) throw new Error(data?.error || 'Não foi possível remover o usuário.')
  return data
}

export async function configureWhatsApp(organizationId, request = {}) {
  if (!isSupabaseConfigured) throw new Error('Configure o Supabase antes de conectar o WhatsApp.')
  const { data, error } = await supabase.functions.invoke('whatsapp-configure', {
    body: { organization_id: organizationId, ...request },
  })
  if (error) {
    let message = error.message
    try { message = (await error.context?.json())?.error || message } catch { /* resposta bloqueada ou sem JSON */ }
    throw new Error(message)
  }
  if (!data?.ok) throw new Error(data?.error || 'Não foi possível configurar o WhatsApp.')
  return data
}

export async function startLeadConversation(leadId) {
  if (!isSupabaseConfigured) throw new Error('Configure o Supabase antes de iniciar um atendimento.')
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
  if (sessionError || !sessionData.session?.access_token) throw new Error('Sua sessão expirou. Entre novamente para continuar.')
  const baseUrl = String(import.meta.env.VITE_SUPABASE_URL || '').replace(/\/+$/, '')
  const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY
  let response
  try {
    response = await fetch(`${baseUrl}/functions/v1/whatsapp-start-conversation`, {
      method: 'POST',
      headers: { apikey: publishableKey, Authorization: `Bearer ${sessionData.session.access_token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ lead_id: leadId }),
    })
  } catch {
    throw new Error('Não foi possível acessar o serviço de atendimento. Verifique o endereço usado para abrir o CRM e tente novamente.')
  }
  const raw = await response.text()
  let data = null
  try { data = raw ? JSON.parse(raw) : null } catch { data = null }
  if (!response.ok) throw new Error(data?.error || `Não foi possível iniciar a conversa (${response.status}).`)
  if (!data?.ok || !data.conversation_id) throw new Error(data?.error || 'Não foi possível iniciar a conversa.')
  return data
}

async function invokeWebhookAdmin(body) {
  if (!isSupabaseConfigured) return { ok: true, integrations: [], endpoint: 'https://SEU-PROJETO.supabase.co/functions/v1/ingest-lead' }
  const { data, error } = await supabase.functions.invoke('integration-webhooks', { body })
  if (error) {
    let message = error.message
    try { message = (await error.context?.json())?.error || message } catch { /* resposta sem JSON */ }
    throw new Error(message)
  }
  if (!data?.ok) throw new Error(data?.error || 'Não foi possível gerenciar o webhook.')
  return data
}

export const listWebhookIntegrations = organizationId => invokeWebhookAdmin({ action: 'list', organization_id: organizationId })
export const createWebhookIntegration = (organizationId, integration) => invokeWebhookAdmin({ action: 'create', organization_id: organizationId, ...integration })
export const revokeWebhookIntegration = (organizationId, integrationId) => invokeWebhookAdmin({ action: 'revoke', organization_id: organizationId, integration_id: integrationId })

export async function getLeadTimeline(leadId) {
  if (!isSupabaseConfigured) return { notes: [], activities: [], history: [], attribution: null, conversationId: null }
  const [notesResult, activitiesResult, historyResult, attributionResult] = await Promise.all([
    supabase.from('lead_notes').select('*, author:profiles!author_id(full_name)').eq('lead_id', leadId).order('created_at', { ascending: false }),
    supabase.from('activities').select('*').eq('lead_id', leadId).order('due_at'),
    supabase.from('lead_stage_history').select('*').eq('lead_id', leadId).order('changed_at', { ascending: false }),
    supabase.from('lead_attributions').select('*').eq('lead_id', leadId).maybeSingle(),
  ])
  const error = notesResult.error || activitiesResult.error || historyResult.error || attributionResult.error
  if (error) throw error
  const rawHistory = historyResult.data || []
  const stageIds = [...new Set(rawHistory.flatMap(item => [item.from_stage_id, item.to_stage_id]).filter(Boolean))]
  const actorIds = [...new Set(rawHistory.map(item => item.changed_by).filter(Boolean))]
  const [stagesResult, actorsResult] = await Promise.all([
    stageIds.length
      ? supabase.from('pipeline_stages').select('id, name').in('id', stageIds)
      : Promise.resolve({ data: [], error: null }),
    actorIds.length
      ? supabase.from('profiles').select('id, full_name').in('id', actorIds)
      : Promise.resolve({ data: [], error: null }),
  ])
  if (stagesResult.error || actorsResult.error) throw stagesResult.error || actorsResult.error
  const stagesById = Object.fromEntries((stagesResult.data || []).map(stage => [stage.id, stage]))
  const actorsById = Object.fromEntries((actorsResult.data || []).map(actor => [actor.id, actor]))
  const history = rawHistory.map(item => ({
    ...item,
    from_stage: item.from_stage_id ? stagesById[item.from_stage_id] || null : null,
    to_stage: stagesById[item.to_stage_id] || null,
    actor: item.changed_by ? actorsById[item.changed_by] || null : null,
  }))
  const movements = history.map(item => ({
    id: `movement-${item.id}`,
    title: item.from_stage?.name
      ? `Mudança de fase: ${item.from_stage.name} → ${item.to_stage?.name || 'nova fase'}`
      : `Entrada em ${item.to_stage?.name || 'nova fase'}`,
    description: [item.reason && `Motivo: ${item.reason}`, item.note && `Observação: ${item.note}`].filter(Boolean).join(' · '),
    due_at: item.changed_at,
    created_at: item.changed_at,
  }))
  const activities = [...(activitiesResult.data || []), ...movements]
    .sort((a, b) => new Date(b.due_at || b.created_at) - new Date(a.due_at || a.created_at))
  const { data: lead, error: leadError } = await supabase.from('leads').select('whatsapp_contact_id, phone_e164, phone').eq('id', leadId).single()
  if (leadError) throw leadError
  let contactId = lead.whatsapp_contact_id
  if (!contactId && (lead.phone_e164 || lead.phone)) {
    const phoneDigits = String(lead.phone_e164 || lead.phone).replace(/\D/g, '')
    const { data: contact, error: contactError } = await supabase.from('whatsapp_contacts').select('id').in('phone_e164', [`+${phoneDigits}`, phoneDigits]).limit(1).maybeSingle()
    if (contactError) throw contactError
    contactId = contact?.id || null
  }
  let conversationId = null
  if (contactId) {
    const { data: conversation, error: conversationError } = await supabase.from('whatsapp_conversations').select('id').eq('contact_id', contactId).order('last_message_at', { ascending: false }).limit(1).maybeSingle()
    if (conversationError) throw conversationError
    conversationId = conversation?.id || null
  }
  return { notes: notesResult.data || [], activities, history, attribution: attributionResult.data || null, conversationId }
}

export function subscribeToLeads(onChange) {
  if (!isSupabaseConfigured || !activeOrganizationId) return () => {}
  const channel = supabase.channel(`crm-leads-${activeOrganizationId}`).on('postgres_changes', {
    event: '*', schema: 'public', table: 'leads', filter: `organization_id=eq.${activeOrganizationId}`,
  }, onChange).subscribe()
  return () => supabase.removeChannel(channel)
}
